import React, { createContext, ReactNode, useContext } from 'react'
import { useRecentApplications } from './api/use-recent-applications'
import { WizardConfig } from './wizard-config'

interface ContextType {
  isWrappedByProvider: boolean
  isConnected: () => boolean
  isCheckingIfConnected: boolean
  checkIfConnected: () => Promise<void>
}

const Context = createContext<ContextType>({
  isWrappedByProvider: false,
  isConnected: () => false,
  isCheckingIfConnected: false,
  checkIfConnected: () => Promise.resolve()
})

export const UseAddAgentConnectionStatusContextProvider = ({
  organizationUuid,
  wizardConfig,
  currentStepIndex,
  children
}: {
  organizationUuid: string
  wizardConfig: WizardConfig
  currentStepIndex: number
  children: ReactNode
}) => {
  // Only enable if the current add agent wizard step is the "connection" step
  const enabled =
    wizardConfig.steps[currentStepIndex] &&
    wizardConfig.steps[currentStepIndex] === 'connection'

  const { recentApplications, isLoading, refetch } = useRecentApplications({
    organizationUuid,
    enabled
  })

  return (
    <Context.Provider
      value={{
        isWrappedByProvider: true,
        isConnected: () =>
          (recentApplications.languages as string[]).indexOf(
            wizardConfig.agent
          ) > -1,
        isCheckingIfConnected: isLoading,
        checkIfConnected: async () => {
          await refetch()
        }
      }}
      children={children}
    />
  )
}

export const useAddAgentConnectionStatus = () => {
  const context = useContext(Context)
  if (!context.isWrappedByProvider) {
    throw new Error(
      `useAddAgentConnectionStatus must be used within a UseAddAgentConnectionStatusContextProvider`
    )
  }
  return context
}
